import { Icon, Tooltip } from '@lightspeed/design-system-react'
import { createSelector } from '@reduxjs/toolkit'
import { TFunction, useT } from '@transifex/react'
import React, { FunctionComponent, useMemo } from 'react'
import {
  canRemoveCustomerFromSale,
  hasCustomerRelatedPayments,
  isAccountBalancePaymentPaid,
  isCompleted,
  isConfirmed,
  isLayby,
  isLegacyServiceSale,
  isOnAccount,
  isUnfulfilled,
} from '../../../functions/sale'
import { hasServiceOrder } from '../../../functions/service'
import { useAppSelector } from '../../../state/hooks'
import {
  ReduxCurrentSale,
  selectCustomFields,
  selectSaleCustomer,
  selectSalePayments,
  selectSaleStatus,
  selectServiceFields,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { RootState } from '../../../state/store'

const selectData = createSelector(
  [
    selectSaleStatus,
    selectSaleCustomer,
    selectSalePayments,
    selectServiceFields,
    selectCustomFields,
    (state: RootState) => state.currentSale.payAccountBalance,
  ],
  (
    status,
    customer,
    payments,
    serviceFields,
    customFields,
    payAccountBalance
  ) => ({
    status,
    customer,
    payments,
    serviceFields,
    customFields,
    payAccountBalance,
  })
)

const getLockedTooltipMessages = (t: TFunction) => ({
  SERVICE_ORDER: {
    title: t('Service sale'),
    message: t(
      'This customer can’t be removed from the sale because it includes a service.'
    ),
  },
  ONACCOUNT: {
    title: t('Sale is on account'),
    message: t(
      'This customer can’t be removed from the sale because it’s on account.'
    ),
  },
  LAYBY: {
    title: t('Sale is on layaway'),
    message: t(
      'This customer can’t be removed from the sale because it’s on layaway.'
    ),
  },
  UNFULFILLED: {
    title: t('Sale has items for fulfillment'),
    message: t(
      'This customer can’t be removed from the sale because there are items that require fulfillment.'
    ),
  },
  CUSTOMER_BALANCE_PAYMENT: {
    title: t('Customer balance used for payment'),
    message: t(
      'This customer can’t be removed from the sale because a partial payment has been made using loyalty or store credit.'
    ),
  },
  ACCOUNT_BALANCE_PAYMENT: {
    title: t('Account balance payment taken'),
    message: t(
      'This customer can’t be removed from the sale because an account balance payment has been taken.'
    ),
  },
})

const isSaleServiceOrder = (
  sale: Pick<ReduxCurrentSale, 'status' | 'serviceFields' | 'customFields'>
) => {
  if (!sale.serviceFields) {
    return false
  }

  return (
    hasServiceOrder(sale.serviceFields, sale.customFields) ||
    isLegacyServiceSale(sale.status)
  )
}

const isSaleInProgressWithCustomerPayments = (
  sale: Pick<ReduxCurrentSale, 'status' | 'customer' | 'payments'>
) => {
  const isNewSale = sale.status === undefined
  const isSaleConfirmed = isConfirmed(sale)
  const isSaleCompleted = isCompleted(sale.status)
  const hasCustomerPayments = hasCustomerRelatedPayments(sale.payments || [])

  return (
    sale.customer &&
    (isNewSale || (!isSaleConfirmed && !isSaleCompleted)) &&
    hasCustomerPayments
  )
}

const useLockedTooltip = (): { title: string; message: string } | null => {
  const t = useT()
  const messages = useMemo(() => getLockedTooltipMessages(t), [t])
  const sale = useAppSelector(selectData)

  if (canRemoveCustomerFromSale(sale)) {
    return null
  }

  if (isSaleServiceOrder(sale)) {
    return messages.SERVICE_ORDER
  }

  if (isOnAccount(sale.status)) {
    return messages.ONACCOUNT
  }

  if (isLayby(sale.status)) {
    return messages.LAYBY
  }

  if (isUnfulfilled(sale.status)) {
    return messages.UNFULFILLED
  }
  if (isSaleInProgressWithCustomerPayments(sale)) {
    return messages.CUSTOMER_BALANCE_PAYMENT
  }

  if (isAccountBalancePaymentPaid(sale.payAccountBalance)) {
    return messages.ACCOUNT_BALANCE_PAYMENT
  }

  return null
}

export const LockedCustomerIcon: FunctionComponent = () => {
  const message = useLockedTooltip()
  if (!message) {
    return null
  }

  return (
    <Tooltip
      direction="center"
      className="wr-tooltip"
      icon="fa-info-circle"
      canBeShown={true}
      header={message.title}
      message={message.message}
    >
      <Icon
        data-testid="customer-locked-icon"
        icon="fa-lock"
        className="vd-pa4"
      />
    </Tooltip>
  )
}
